import * as React from 'react';
import Head from 'next/head';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Typography from '@/components/sushi/Typography';
import { useIntl } from 'react-intl';
import { TextShadowH1 } from '@/components/TextShadow/TextShadow';
import { styled } from 'stitches.config';
import { ComplianceDisclaimer } from '@/components/simulators/ComplianceDisclaimer';
import { Simulator } from '@/components/WealthSimulator/Simulator';
import { NextSeo } from 'next-seo';
import { useRouter } from 'next/router';
import {
  SeoTextWithNavigation,
  SeoTextWithNavigationEntry,
} from '@/components/Seo/SeoTextWithNavigation';

const WealthSimulator = () => {
  const { formatMessage } = useIntl();
  const router = useRouter();

  const seoEntries: SeoTextWithNavigationEntry[] = [
    {
      id: 'how-it-works',
      title: formatMessage({ id: `wealth_simulator.content.block1.title` }),
      text: formatMessage({ id: `wealth_simulator.content.block1.text` }),
    },
    {
      id: 'understand-simulator-resuts',
      title: formatMessage({ id: `wealth_simulator.content.block2.title` }),
      text: formatMessage({ id: `wealth_simulator.content.block2.text` }),
    },
    {
      id: 'get-to-financial-independence',
      title: formatMessage({ id: `wealth_simulator.content.block3.title` }),
      text: formatMessage({ id: `wealth_simulator.content.block3.text` }),
    },
  ];

  if (router.locale === 'de') {
    seoEntries.push({
      id: 'create-enterprise-wealth',
      title: formatMessage({ id: `wealth_simulator.content.blockDeLinkExchange.title` }),
      text: formatMessage({ id: `wealth_simulator.content.blockDeLinkExchange.text` }),
    });
  }

  return (
    <>
      <NextSeo
        title={formatMessage({ id: 'wealth_simulator.meta.title' })}
        description={formatMessage({ id: 'wealth_simulator.meta.description' })}
        canonical={`${process.env.NEXT_PUBLIC_PUBLIC_PATH}/${router.locale}/tools/wealth-simulator`}
        openGraph={{
          type: 'website',
          url: `${process.env.NEXT_PUBLIC_PUBLIC_PATH}/${router.asPath}/`,
          title: formatMessage({ id: 'wealth_simulator.meta.title' }),
          description: formatMessage({ id: 'wealth_simulator.meta.description' }),
          images: [
            {
              url: `${process.env.NEXT_PUBLIC_PUBLIC_PATH}/img/wealth-simulator/${router.locale}/simulation.png`,
              alt: formatMessage({ id: 'homepage.meta.preview' }),
            },
          ],
        }}
      />
      <Wrapper id="home-wrapper">
        <Head>
          <meta
            name="twitter:title"
            content={formatMessage({ id: 'wealth_simulator.meta.title' })}
          />
          <meta
            name="twitter:description"
            content={formatMessage({ id: 'wealth_simulator.meta.twitter_card' })}
          />
          <meta name="twitter:card" content="summary_large_image" />
          <meta
            name="twitter:image"
            content={`${process.env.NEXT_PUBLIC_PUBLIC_PATH}/img/wealth-simulator/${
              router.locale === 'de' ? 'en' : router.locale
            }/simulation.png`}
          />
        </Head>
        <Header />
        <Hero>
          <Title>
            <Typography variant="headingM" as={TextShadowH1} center>
              {formatMessage({ id: 'wealth_simulator.title' })}
            </Typography>
            <Typography variant="bodyM" color="secondary" center font="montreal">
              {formatMessage({ id: 'wealth_simulator.subtitle' })}
            </Typography>
          </Title>

          <Simulator />

          <DisclaimerSlot>
            <ComplianceDisclaimer />
          </DisclaimerSlot>
        </Hero>
        <Content>
          <SeoTextWithNavigation entries={seoEntries} />
        </Content>
        <div>
          <Footer bannerTitle="app.footer.bannerTitle" />
        </div>
      </Wrapper>
    </>
  );
};

const Wrapper = styled('div', {
  overflow: 'hidden auto',
  backgroundColor: 'white',
  backgroundSize: '100% 1440px',
  backgroundImage: 'url(/img/tools/wealth-simulator-background.png)',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'center top',

  '@lg': {
    backgroundSize: '1335px 1440px',
  },
});

const Hero = styled('div', {
  paddingTop: '160px',
  textAlign: 'center',
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  maxWidth: '1440px',
  margin: 'auto',
  '@sm': {
    paddingTop: '112px',
  },
});

const Title = styled('div', {
  padding: '0 32px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  '> *:first-child': {
    marginBottom: '12px',
    '@sm': {
      marginBottom: '24px',
    },
  },
  '> *:not(:first-child)': {
    maxWidth: '900px',
  },
});

// Mirrors the Simulator's `Container` insets so the disclaimer block lines up with
// the chart above on desktop (112px gutters) and goes edge to edge on mobile.
const DisclaimerSlot = styled('div', {
  alignSelf: 'stretch',
  margin: '16px 112px 112px',
  '@sm': {
    margin: '16px 0 40px',
  },
});

const Content = styled('div', {
  width: '100%',
  maxWidth: 1264,
  margin: '0 auto',
  padding: '24px',
});

export default WealthSimulator;
